import React, { useEffect, useRef, useState } from 'react';
import { Loader2 } from 'lucide-react';
import { fetchSupportChatAttachmentBlob } from '../../services/supportChatService';
import { cn } from '../../lib/utils';
import BlobImageLightbox from '../BlobImageLightbox';

/** Start fetching an image when it is this close to the visible part of the thread. */
const LAZY_LOAD_MARGIN = '600px 0px';

/**
 * Authenticated chat image: fetched as a blob (same pattern as SmsMediaImage).
 * Deferred until the placeholder nears the scroll area, so opening a long
 * thread only fetches the latest images; older ones load as the user scrolls up.
 * The thread shows the server's small preview; the full image is fetched when
 * the lightbox opens.
 * scrollRootRef: ref to the thread's scroll container (IntersectionObserver root).
 */
const SupportChatAttachmentImage = ({ threadEmail, fileId, alt = 'Screenshot', className, scrollRootRef }) => {
  const placeholderRef = useRef(null);
  const [inView, setInView] = useState(
    () => typeof window === 'undefined' || typeof window.IntersectionObserver === 'undefined'
  );
  const [objectUrl, setObjectUrl] = useState(null);
  const [failed, setFailed] = useState(false);
  const [fullObjectUrl, setFullObjectUrl] = useState(null);
  const fullRequestedRef = useRef(false);

  useEffect(() => {
    if (inView) return undefined;
    const target = placeholderRef.current;
    if (!target) return undefined;
    const observer = new IntersectionObserver(
      (entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          setInView(true);
          observer.disconnect();
        }
      },
      { root: scrollRootRef?.current || null, rootMargin: LAZY_LOAD_MARGIN }
    );
    observer.observe(target);
    return () => observer.disconnect();
  }, [inView, scrollRootRef]);

  useEffect(() => {
    if (!inView) return undefined;
    let cancelled = false;
    let activeUrl = null;

    setFailed(false);
    setObjectUrl(null);

    fetchSupportChatAttachmentBlob(threadEmail, fileId, { thumbnail: true })
      .then((blob) => {
        if (cancelled) return;
        activeUrl = URL.createObjectURL(blob);
        setObjectUrl(activeUrl);
      })
      .catch(() => {
        if (!cancelled) setFailed(true);
      });

    return () => {
      cancelled = true;
      if (activeUrl) URL.revokeObjectURL(activeUrl);
    };
  }, [inView, threadEmail, fileId]);

  useEffect(() => () => {
    if (fullObjectUrl) URL.revokeObjectURL(fullObjectUrl);
  }, [fullObjectUrl]);

  const loadFullImage = () => {
    if (fullRequestedRef.current) return;
    fullRequestedRef.current = true;
    fetchSupportChatAttachmentBlob(threadEmail, fileId)
      .then((blob) => setFullObjectUrl(URL.createObjectURL(blob)))
      .catch(() => {
        // Keep showing the preview; the next open retries.
        fullRequestedRef.current = false;
      });
  };

  if (failed) {
    return (
      <div className={cn('rounded-md bg-black/5 dark:bg-white/5 px-3 py-2 text-xs text-muted-foreground', className)}>
        Image unavailable
      </div>
    );
  }

  if (!objectUrl) {
    // Fixed height so unloaded images above the viewport keep the scroll position steady.
    return (
      <div
        ref={placeholderRef}
        className={cn('flex items-center justify-center rounded-md bg-black/5 dark:bg-white/5 h-40 min-w-[8rem]', className)}
      >
        {inView ? <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" /> : null}
      </div>
    );
  }

  return (
    <BlobImageLightbox
      objectUrl={objectUrl}
      fullObjectUrl={fullObjectUrl}
      onOpen={loadFullImage}
      alt={alt}
      thumbClassName={cn('max-h-56', className)}
    />
  );
};

export default SupportChatAttachmentImage;
